import { cn } from "@/lib/utils";
import { ProofBadge } from "./ProofBadge";
import { PostActions } from "./PostActions";
import { Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, AlertCircle, Pencil } from "lucide-react";

interface CommunityPostProps {
  id: string;
  author: {
    name: string;
    avatar: string;
    title: string;
    verified: boolean;
    username?: string;
  };
  category: string;
  title: string;
  description: string;
  stats: {
    likes: number;
    comments: number;
  };
  proofType: "verified" | "pending" | "community";
  image?: string;
  timeAgo: string;
  isOwner?: boolean;
  isApproved?: boolean;
  moderationReason?: string;
  className?: string;
  onClick?: () => void;
  onEdit?: () => void;
}

export const CommunityPost = ({
  id,
  author,
  category,
  title,
  description,
  stats,
  proofType,
  image,
  timeAgo,
  isOwner,
  isApproved,
  moderationReason,
  className,
  onClick,
  onEdit,
}: CommunityPostProps) => {
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onEdit?.();
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        "bg-card border border-border rounded-lg overflow-hidden transition-all duration-200 hover:shadow-card",
        onClick && "cursor-pointer",
        className
      )}
    >
      {/* Image */}
      {image && (
        <div className="aspect-video bg-secondary overflow-hidden">
          <img
            src={image}
            alt={title}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <div className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-center gap-2.5 sm:gap-3 mb-2.5 sm:mb-3">
          {author.username ? (
            <Link to={`/u/${author.username}`} onClick={(e) => e.stopPropagation()}>
              <img
                src={author.avatar}
                alt={author.name}
                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover hover:ring-2 hover:ring-primary transition-all flex-shrink-0"
              />
            </Link>
          ) : (
            <img
              src={author.avatar}
              alt={author.name}
              className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 sm:gap-2">
              {author.username ? (
                <Link 
                  to={`/u/${author.username}`}
                  onClick={(e) => e.stopPropagation()}
                  className="font-semibold text-foreground text-sm hover:text-primary transition-colors truncate"
                >
                  {author.name}
                </Link>
              ) : (
                <span className="font-semibold text-foreground text-sm truncate">
                  {author.name}
                </span>
              )}
              {author.verified && (
                <ProofBadge type="verified" />
              )}
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2 text-xs text-muted-foreground">
              <span className="truncate max-w-[100px] sm:max-w-none">{author.title}</span>
              <span className="flex-shrink-0">•</span>
              <span className="flex-shrink-0">{timeAgo}</span>
            </div>
          </div>
        </div>

        {/* Category and Moderation Status */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="inline-block px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded">
            {category}
          </span>
          {/* Owner moderation status indicators */}
          {isOwner && !isApproved && moderationReason && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-destructive bg-destructive/10 rounded">
              <XCircle className="w-3 h-3" />
              Rejected
            </span>
          )}
          {isOwner && !isApproved && !moderationReason && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-600 bg-amber-500/10 rounded">
              <Loader2 className="w-3 h-3 animate-spin" />
              Pending
            </span>
          )}
          {isOwner && isApproved && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-green-600 bg-green-500/10 rounded">
              <CheckCircle className="w-3 h-3" />
              Approved
            </span>
          )}
        </div>

        {/* Rejection reason for owner */}
        {isOwner && !isApproved && moderationReason && (
          <div className="flex items-start gap-2 p-2 bg-destructive/10 rounded-lg mb-3 text-xs">
            <AlertCircle className="w-3.5 h-3.5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <span className="text-destructive font-medium">Rejected: </span>
              <span className="text-muted-foreground">{moderationReason}</span>
            </div>
          </div>
        )}

        {/* Content */}
        <h3 className="font-semibold text-foreground text-sm sm:text-base mb-1.5 sm:mb-2 line-clamp-2">{title}</h3>
        <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mb-3 sm:mb-4">
          {description}
        </p>

        {/* Footer */}
        <div className="pt-2.5 sm:pt-3 border-t border-border flex items-center justify-between gap-2">
          <PostActions
            postId={id}
            initialLikes={stats.likes}
            initialComments={stats.comments}
            onCommentClick={onClick}
            compact
          />
          {isOwner && !isApproved && moderationReason && (
            <button
              onClick={handleEditClick}
              className="text-xs font-medium text-amber-600 hover:underline flex items-center gap-1 flex-shrink-0 p-1 touch-manipulation"
            >
              <Pencil className="w-3 h-3" />
              <span className="hidden xs:inline">Edit & Resubmit</span>
              <span className="xs:hidden">Edit</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
